import { DeviceType } from '../../../types/base/affiliate/characteristics';
import { AffiliateImage } from '../../../types/base/affiliate/image';
import { AffiliateControllerResponse } from '../../../types/controllers/affiliate';
import { getQueryParamString } from '../../../util/string';
import { FormattedPromotion } from '../types';

declare global {
    interface Window {
        OnetrustActiveGroups?: string;
        OneTrust?: {
            GetDomainData: () => { Groups: Array<{ OptanonGroupId: string }> };
        };
        /** Demo-page override so local BAM can be used before this mapping is deployed. */
        BAM_API_BASE_URL?: string;
    }
}

type ImageCategoryMapping = {
    [K in 'thumbnail' | 'logo' | 'promo' | 'desktop_banner' | 'mobile_banner' | 'app_game_page_ad']: number;
};

export enum LazyLoadClassName {
    CTA_LOGO = 'logo',
    LIST_LOGO = 'item__logo',
    BANNER_IMAGE = 'image',
    IMPRESSION_PIXEL = 'bam__pixel',
}

const categoryIdMapping: ImageCategoryMapping = {
    thumbnail: 1,
    logo: 2,
    promo: 4,
    desktop_banner: 6,
    mobile_banner: 7,
    app_game_page_ad: 8,
};

const switchboardDomainMapping = {
    1: 'switchboard.actionnetwork.com',
    2: 'switchboard.vegasinsider.com',
    3: 'switchboard.rotogrinders.com',
    4: 'switchboard.scoresandodds.com',
};

export const getBaseUrl = (devMode: boolean): string => {
    if (typeof window !== 'undefined' && window.BAM_API_BASE_URL) {
        return window.BAM_API_BASE_URL;
    }

    return devMode ? `https://bam-stage.actionnetwork.com` : `https://b.bet-links.com`;
};

export const getBaseActionUrl = (devMode: boolean): string => {
    return devMode ? `https://api-stage.actionnetwork.com` : `https://api.actionnetwork.com`;
};

export const getBaseDeeplinkUrl = (devMode: boolean): string => {
    return devMode ? 'https://switchboard-dev.actionnetwork.com' : 'https://switchboard.actionnetwork.com';
};

export const getBaseSwitchboardUrl = (propertyId: number): string => {
    if (Object.keys(switchboardDomainMapping).includes(propertyId.toString())) {
        return switchboardDomainMapping[propertyId];
    }
    return switchboardDomainMapping[1];
};

export const createSwitchboardLink = (domain: string | null, params: Record<string, unknown>): string => {
    domain = domain || 'switchboard.actionnetwork.com';
    if (!params.consentIds && window && window.OnetrustActiveGroups && window.OneTrust) {
        const otActiveGroups = window.OnetrustActiveGroups.split(',').filter(Boolean);
        const otGroups = window.OneTrust.GetDomainData().Groups.map(group => {
            return group.OptanonGroupId;
        });
        const otDeniedGroups = otGroups.filter((group: string) => !otActiveGroups.includes(group));

        params = { ...params, consentIds: otActiveGroups.join(','), denyIds: otDeniedGroups.join(',') };
    }
    return `https://${domain}/offers${getQueryParamString(params)}`;
};

export const getQueryParamFromUrl = (param: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
};

export function getQueryString(
    params: Record<string, undefined | null | number | string | boolean | Array<number | string>>
) {
    let s = '';
    params = params || {};

    Object.keys(params).forEach(paramKey => {
        const paramValue = params[paramKey];
        if (
            paramValue === null ||
            paramValue === undefined ||
            paramValue === '' ||
            (Array.isArray(paramValue) && paramValue.length === 0)
        ) {
            return;
        }

        if (s) {
            s = `${s}&${paramKey}=${paramValue.toString()}`;
        } else {
            s = `?${paramKey}=${paramValue.toString()}`;
        }
    });
    return s;
}

const paidMarketingParams = [
    'action0050',
    'action0062',
    'action0075',
    'action0090',
    'action0064',
    'action0516',
    'VI0045',
    'RG0023',
    'FL0029',
];

export const isValidPaidMarketingParam = (param: string): boolean => {
    return paidMarketingParams.includes(param);
};

export const getPromotionImage = (images: AffiliateImage[], category: keyof ImageCategoryMapping) =>
    images.find(image => image.image_type?.id === categoryIdMapping[category])?.image_url;

export const formatPromotions = (
    promotions: AffiliateControllerResponse['promotions'],
    {
        allowAltLink,
        promotionListId,
        location,
        context,
        propertyId,
        reviewContext,
        i18n,
    }: {
        allowAltLink: boolean;
        promotionListId: number | null;
        location: string;
        context: string;
        propertyId: number;
        reviewContext?: string;
        i18n?: Record<string, string>;
    }
): FormattedPromotion[] => {
    if (!context) {
        // eslint-disable-next-line no-console
        console.warn('BAM Warning: Missing context');
    }

    const paidMarketing = getQueryParamFromUrl('paidMarketing');

    return promotions.map(promo => {
        const { button_text: promoButtonText, bonus_code: bonusCode } = promo.additional_attributes;
        const amount = promo.dollar_amount || promo.campaign.dollar_amount || 0;

        const claimString = i18n?.claim || 'Claim';
        const learnMoreString = i18n?.learnMore || 'Learn More';
        const buttonText = promoButtonText ? promoButtonText : amount ? `${claimString} $${amount}` : learnMoreString;
        const alternateLink = allowAltLink && promo.alternate_link;
        const secondaryLink = promo.additional_attributes.secondary_property_link || null;
        const impressionUrls = promo.additional_attributes.impression_urls_meta || [];
        const link =
            alternateLink ||
            createSwitchboardLink(promo.switchboard_domain || null, {
                affiliateId: promo.affiliate.id,
                campaignId: promo.campaign.id,
                stateCode: location,
                context: context,
                propertyId,
                options: paidMarketing,
            });
        const reviewPageLink = createSwitchboardLink(promo.switchboard_domain || null, {
            affiliateId: promo.affiliate.id,
            campaignId: promo.campaign.id,
            stateCode: location,
            context: reviewContext,
            propertyId,
            options: paidMarketing,
        });

        promo.images = processPromoImages(promo.images);
        promo.value_props = processValueProps(promo);

        return {
            id: promo.id || 0,
            link,
            secondaryLink,
            buttonText,
            logo: getPromotionImage(promo.images, 'thumbnail') || '',
            largeLogo: getPromotionImage(promo.images, 'promo') || '',
            bookName: promo.affiliate.display_name || '',
            bookDataName: promo.affiliate.name || '',
            bookType: promo.affiliate.affiliate_type || '',
            logoColor: promo.affiliate.primary_color || '',
            cta: promo.title || '',
            impressionUrls,
            termsText: promo.terms || '',
            bonusCode: bonusCode || '',
            props: promo.value_props || [],
            bookId: promo.affiliate.id,
            campaignId: promo.campaign.id,
            promotionListId,
            images: promo.images,
            valueProps: promo.value_props,
            isQuickslip: promo.additional_attributes.is_quickslip || false,
            internalIdentifiers: promo.internal_identifiers,
            minAge: promo.min_age,
            affiliateTerms: promo.affiliate.terms || '',
            reviewLink: reviewPageLink,
            impressionTtl: promo.affiliate.impression_ttl || 86400,
            impressionThrottle: promo.affiliate.impression_throttle || 1,
            impressionThrottleClick:
                promo.affiliate.impression_throttle_click || promo.affiliate.impression_throttle || 1,
            affiliateId: promo.affiliate.id,
            rating: promo.affiliate.additional_attributes?.rating || 5,
        };
    });
};

function processValueProps(promo: AffiliateControllerResponse['promotions'][0]): string[] {
    if (promo.value_props && promo.value_props.length > 0) {
        return promo.value_props;
    }

    if (promo.affiliate.additional_attributes && promo.affiliate.additional_attributes.features) {
        return promo.affiliate.additional_attributes.features;
    }

    return [];
}

function processPromoImages(images: any[]) {
    const domain = 'assets.actionnetwork.com';
    const newDomain = 'assets.bet-links.com';

    images.forEach(image => {
        if (image.image_url.includes(domain)) {
            image.image_url = image.image_url.replace(new RegExp(domain, 'g'), newDomain);
        }
    });

    return images;
}

export function createSrcSet(src: string | null, width: string | number, height: string | number): string {
    if (!src) {
        return '';
    }
    const [rawSrc] = snipFileType(src);
    const parsedWidth = typeof width === 'number' ? width : parseInt(width, 10);
    const parsedHeight = typeof height === 'number' ? height : parseInt(height, 10);

    return [4, 3, 2, 1.5, 1, 0.5]
        .map(
            divisor =>
                `${getResizedImage(
                    rawSrc,
                    divideIt(parsedWidth, divisor),
                    divideIt(parsedHeight, divisor)
                )}.webp ${divideIt(parsedWidth, divisor)}w`
        )
        .join(',');
}

function snipFileType(str: string): Array<string> {
    const regex = /\.(\w+)$/;
    const ext = str.match(regex) || [];
    return [str.replace(regex, ''), ext[1]];
}

export function getResizedImage(url: string, width: string | number, height: string | number): string {
    return url.replace(
        /https:\/\/(assets).bet-links.com\/(blog)?/,
        (_, subdomain, blog) =>
            `https://${subdomain}.bet-links.com/${width}x${height}/${blog !== undefined ? blog : ''}`
    );
}

function divideIt(number: number, divisor: number): string {
    return (number / divisor).toFixed();
}

export const generateColorUri = (color: string, width: number, height: number) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.fillStyle = color;

        ctx.fillRect(0, 0, width, height);
    }

    return '';
};

export function determineDeviceType(): DeviceType {
    const userAgent = navigator.userAgent;

    if (/android/i.test(userAgent)) {
        return 'android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
        return 'ios';
    }

    return 'desktop';
}

export function requiredParamsCheck(params: { [param: string]: number | string }, componentType?: string): boolean {
    const errors = [];

    Object.keys(params).forEach(param => {
        if (!params[param]) {
            errors.push(`${param} is missing`);
        }
    });

    logBamErrors(errors, componentType);

    return errors.length > 0;
}

export function logBamErrors(errors: Array<string>, component?: string): void {
    const componentType = component ? `${component} - ` : '';

    errors.forEach(error => {
        // eslint-disable-next-line no-console
        console.error(`BAM Error: ${componentType}${error}`);
    });
}
